/**
 * @fileoverview Browser telemetry client — thin wrapper over the Application Insights
 * web SDK adding an offline buffer, property enrichment, cloud-role tagging, env-aware
 * sampling and browser↔backend correlation (RUM).
 * @module web-telemetry/client
 *
 * Responsibilities:
 * - Initialize the Application Insights web SDK with full RUM (auto route tracking,
 *   page views, AJAX/fetch dependency tracking, exceptions) and CORS correlation.
 * - Tag every item with the app's cloud role so browser telemetry appears alongside
 *   the backend roles in the Application Map.
 * - Enrich every event with shared context (set via setGlobalProperties) + online state.
 * - Buffer events while offline and replay them on reconnect.
 * Dependencies:
 * - @microsoft/applicationinsights-web — the browser SDK.
 * - ./telemetryBuffer — offline persistence.
 * - ./sampling — env-aware sampling percentage.
 * Architecture:
 * - App-agnostic base: mobile-pwa, tenant-web and system-web each own a singleton
 *   (mobile-pwa subclasses it to add domain event helpers). Connection string + cloud
 *   role + env are supplied by each app's config at init time — nothing is hardcoded.
 * Notes:
 * - initialize() is safe to call before a connection string exists: telemetry is simply
 *   disabled (no-op) and the app keeps running. It never throws.
 */
import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';
import { TelemetryBuffer } from './telemetryBuffer';
import { resolveBrowserSamplingPercentage } from './sampling';

/**
 * Third-party cross-origin domains that must NEVER receive App Insights
 * correlation headers (`Request-Id` / `traceparent` / `Request-Context`).
 *
 * With `enableCorsCorrelation: true` the SDK injects those headers onto every
 * cross-origin fetch it wraps. Mapbox's `/v4/` tile API rejects unknown headers
 * in its CORS preflight (the 204 carries no `Access-Control-Allow-Headers`), so
 * the browser blocks the request. Because satellite raster tiles are fetched on
 * the main thread (SDK-wrapped), that block surfaces as a failed tile load — a
 * synthetic 503 via the PWA service worker on mobile-pwa, or a raw CORS error on
 * tenant-web / system-web. Excluding Mapbox keeps those requests "simple" (no
 * preflight). First-party APIs are same-origin, so this does not affect
 * browser↔backend correlation.
 */
export const DEFAULT_CORRELATION_HEADER_EXCLUDED_DOMAINS: readonly string[] = [
  '*.mapbox.com',
  'api.mapbox.com',
];

/**
 * @contract WebTelemetryInitOptions
 * Purpose: Inputs required to boot the browser telemetry client.
 * Fields:  connectionString, appEnv, cloudRole, appVersion?, enableAutoRouteTracking?,
 *          samplingPercentage? (override), correlationHeaderExcludedDomains?.
 * Rules:   empty connectionString ⇒ telemetry disabled (no-op, no throw).
 *          correlationHeaderExcludedDomains is MERGED with (not replacing) the
 *          built-in DEFAULT_CORRELATION_HEADER_EXCLUDED_DOMAINS (Mapbox etc.).
 * Sync:    n/a (config object).
 */
export interface WebTelemetryInitOptions {
  connectionString: string;
  appEnv: string;
  cloudRole: string;
  appVersion?: string;
  enableAutoRouteTracking?: boolean;
  samplingPercentage?: number;
  correlationHeaderExcludedDomains?: string[];
}

export class WebTelemetry {
  protected appInsights: ApplicationInsights | null = null;
  protected buffer: TelemetryBuffer;
  protected initialized = false;
  protected appVersion = 'unknown';
  private globalProperties: Record<string, string> = {};

  constructor(bufferStorageKey?: string) {
    this.buffer = new TelemetryBuffer(bufferStorageKey);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.replayBuffer());
    }
  }

  /**
   * @process initialize
   * Steps:   guard double-init → disable if no connection string → construct SDK with
   *          RUM + CORS correlation + env sampling → register cloud-role initializer →
   *          load SDK → track initial page view → replay any buffered events.
   * Inputs:  WebTelemetryInitOptions.
   * Outputs: void (async to preserve existing `await telemetry.initialize()` call sites).
   * Failure Handling: any SDK error is logged and telemetry is left disabled; never throws.
   * Idempotency: REQUIRED (second call is a no-op).
   * @logging events: telemetry init/disable (console, level warn/error); Correlation: cloudRole.
   */
  async initialize(options: WebTelemetryInitOptions): Promise<void> {
    if (this.initialized) return;
    this.appVersion = options.appVersion || 'unknown';

    if (!options.connectionString) {
      console.warn('[telemetry] No connection string — telemetry disabled');
      this.initialized = true;
      return;
    }

    try {
      const samplingPercentage =
        options.samplingPercentage ?? resolveBrowserSamplingPercentage(options.appEnv);

      const ai = new ApplicationInsights({
        config: {
          connectionString: options.connectionString,
          enableAutoRouteTracking: options.enableAutoRouteTracking ?? true,
          enableCorsCorrelation: true,
          disableFetchTracking: false,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          autoTrackPageVisitTime: true,
          disablePageUnloadEvents: ['unload'],
          samplingPercentage,
          maxBatchInterval: 15000,
          correlationHeaderExcludedDomains: [
            ...DEFAULT_CORRELATION_HEADER_EXCLUDED_DOMAINS,
            ...(options.correlationHeaderExcludedDomains ?? []),
          ],
        },
      });

      ai.loadAppInsights();

      // Cloud role — lets browser telemetry appear as its own node in the App Map
      // alongside the backend roles (system-api / tenant-api / mobile-api / workers).
      const cloudRole = options.cloudRole;
      const cloudRoleInstance = `${options.cloudRole}-${options.appEnv}`;
      ai.addTelemetryInitializer((item: ITelemetryItem) => {
        item.tags = item.tags || {};
        item.tags['ai.cloud.role'] = cloudRole;
        item.tags['ai.cloud.roleInstance'] = cloudRoleInstance;
      });

      ai.trackPageView();
      this.appInsights = ai;
      this.initialized = true;

      this.replayBuffer();
    } catch (err) {
      console.error('[telemetry] Failed to initialize:', err);
      this.initialized = true;
    }
  }

  /**
   * @process setGlobalProperties
   * Purpose: merge shared context (e.g. userId, tenantSlug, deviceId) onto every event.
   * Inputs:  partial string map.
   * Outputs: void.
   * Idempotency: REQUIRED (later keys overwrite earlier ones).
   */
  setGlobalProperties(properties: Record<string, string>): void {
    this.globalProperties = { ...this.globalProperties, ...properties };
  }

  /** Associate the authenticated user with subsequent telemetry (App Insights user context). */
  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (userId) this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
  }

  /** Clear the authenticated user (call on sign-out). */
  clearAuthenticatedUser(): void {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  trackEvent(
    name: string,
    properties: Record<string, string> = {},
    measurements: Record<string, number> = {},
  ): void {
    const enriched = this.enrichProperties(properties);
    if (this.appInsights && this.isOnline()) {
      this.appInsights.trackEvent({ name, properties: enriched, measurements });
    } else {
      this.buffer.add({ name, properties: enriched, measurements, timestamp: new Date().toISOString() });
    }
  }

  trackException(error: Error, properties: Record<string, string> = {}): void {
    const enriched = this.enrichProperties(properties);
    if (this.appInsights && this.isOnline()) {
      this.appInsights.trackException({ exception: error, properties: enriched });
    } else {
      this.buffer.add({
        name: 'exception',
        properties: { ...enriched, errorMessage: error.message, errorStack: error.stack || '' },
        measurements: {},
        timestamp: new Date().toISOString(),
      });
    }
  }

  trackMetric(name: string, value: number, properties: Record<string, string> = {}): void {
    const enriched = this.enrichProperties(properties);
    if (this.appInsights && this.isOnline()) {
      this.appInsights.trackMetric({ name, average: value }, enriched);
    } else {
      this.buffer.add({ name: `metric:${name}`, properties: enriched, measurements: { value }, timestamp: new Date().toISOString() });
    }
  }

  trackPageView(name?: string, uri?: string): void {
    this.appInsights?.trackPageView({ name, uri });
  }

  /** Force the SDK to send queued telemetry (e.g. before unload). */
  flush(): void {
    this.appInsights?.flush();
  }

  get bufferSize(): number {
    return this.buffer.size;
  }

  /**
   * @process replayBuffer
   * Steps:   if online + initialized, drain the offline buffer into the SDK, tagging
   *          each replayed event with `_buffered=true`.
   * Inputs:  none.
   * Outputs: void.
   * Failure Handling: no-op when SDK unavailable (events stay buffered).
   * Idempotency: REQUIRED (buffer is emptied atomically by flush()).
   */
  protected replayBuffer(): void {
    if (!this.appInsights || !this.isOnline()) return;
    const buffered = this.buffer.flush();
    for (const event of buffered) {
      this.appInsights.trackEvent({
        name: event.name,
        properties: { ...event.properties, _buffered: 'true' },
        measurements: event.measurements,
      });
    }
  }

  /**
   * @process enrichProperties
   * Purpose: attach shared context + online state + app version to every event.
   * Inputs:  caller properties.
   * Outputs: merged property map.
   * Idempotency: REQUIRED (pure merge).
   */
  protected enrichProperties(properties: Record<string, string>): Record<string, string> {
    return {
      ...this.globalProperties,
      ...properties,
      isOnline: String(this.isOnline()),
      appVersion: this.appVersion,
    };
  }

  private isOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine;
  }
}
